/**
 * MembershipTemplateForm — the add / edit modal for Admin > Memberships and
 * Admin > Packages: the corporate dashboard's membership-template dialog
 * (components/memberships-table.tsx) for one account, in its order.
 *
 *   Left: the image (required while Available in Store is on) and the
 *   Facilities checklist (All / None), in an amber box while none is picked.
 *   Right: Title *, Description, Available in Store, then —
 *     memberships: Membership Agreement;
 *     both: the Products and Procedures lists (each row opens to its fields:
 *       a product as corp's — category, product and expiry stacked with fee,
 *       quantity and taxable beside them; a procedure as legacy's
 *       (lib/admin/membership-procedures-display.php) — Category, Sub Category
 *       and Procedure stacked with the Description beside them, then Hours,
 *       Normal and Effective price. Picking the procedure fills in both prices
 *       from its catalog price, the Hours band and the Description. Legacy
 *       shows no expiry on a procedure, so none is shown; a saved one is kept);
 *     packages: Availability Settings (type, who can buy, dates, limits);
 *     memberships: Cancellation Policy (required duration, notice);
 *     both: Tags (with their icons) and Plan types;
 *     packages: Product categories (those inherited from the products' types,
 *       shown read-only, plus any chosen directly).
 *   Footer: corp's required / suggestion strips, Unsaved changes + Revert,
 *   the Total price, Cancel and Create / Update. No Delete in the modal.
 *
 * A procedure's Hours and Description save (expNumHours, laymansTerms), as
 * legacy's do. Taxable and the procedure's normal price load as saved (corp
 * read them wrong), and emptied lists save as empty.
 *
 * On a Locked page AdminFormModal makes it a viewer; this form also drops its
 * Add product / procedure, remove and Select All / Deselect All buttons, keeps
 * the footer's Total read-only, and its item rows still open to read.
 */
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Button } from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/Checkbox';
import { DatePicker } from '@/components/ui/DatePicker';
import { Input } from '@/components/ui/Input';
import { SearchableCombobox } from '@/components/ui/SearchableCombobox';
import { SearchableMultiSelect } from '@/components/ui/SearchableMultiSelect';
import { Select } from '@/components/ui/Select';
import { Spinner } from '@/components/ui/Spinner';
import { cn } from '@/lib/utils';
import { listFacilities } from '@/services/admin.service';
import { getErrorMessage } from '@/services/apiClient';
import { listProductCategoriesLookup } from '@/services/inventory.service';
import { groupAgreements, listAgreementVersions } from '@/services/membershipAgreements.service';
import {
  createTemplate,
  getPackageAvailability,
  getTemplate,
  listProcedureHourBands,
  listProcedureTree,
  listProductTypeCategoryLinks,
  savePackageAvailability,
  updateTemplate,
  type PackageAvailability,
  type ProcedureCategoryOption,
  type ProductTypeCategoryLink,
  type TemplateDetail,
  type TemplateKind,
  type TemplatePayload,
} from '@/services/membershipTemplates.service';
import { listAdminPatientTags, type AdminPatientTag } from '@/services/patientTags.service';
import { listAdminPlanTypes } from '@/services/planTypes.service';
import { listAllProducts } from '@/services/products.service';
import { toast } from '@/store/toastStore';
import { AdminFormModal } from './AdminFormModal';
import { AdminLockedFieldset } from './AdminLockedFieldset';
import { useAdminPageLevel } from './AdminPageLevelContext';
import { CheckboxList, DESCRIPTION_CLASS, DialogField, LABEL_CLASS, SelectedRows, SwitchField, fieldClasses } from './DialogChrome';
import { RichDescriptionField } from './RichTextDialog';
import { EntityImageTile } from './EntityImageTile';
import { TagIconImage } from './TagIconPicker';
import { ItemRow, ItemsBox, ProcedureFields, procedureNameOf } from './TemplateItemRows';
import { useTrackedForm } from './useTrackedForm';

// ─── Form ──────────────────────────────────────────────────────────────────────

type ExpireUnit = 'days' | 'months' | 'years';

interface ProductRow {
  key: string;
  productID: number | null;
  category: string;
  quantity: string;
  curRate: string;
  taxable: boolean;
  expire: string;
  unit: ExpireUnit;
}

interface ProcedureRow {
  key: string;
  categoryID: number | null;
  /** Sub category: legacy's `procID`. */
  procID: number | null;
  procSubID: number | null;
  /** The saved names, shown when the catalog no longer lists them (retired). */
  savedName: string;
  savedCategoryName: string;
  savedSubcategoryName: string;
  description: string;
  hours: number;
  price: string;
  effectivePrice: string;
  /** Not shown (legacy has no procedure expiry); kept so saving does not wipe it. */
  expireDays: number;
  expireMonths: number;
}

/** Where a procedure sits in the catalog tree, or null when it is not listed. */
type ProcedurePlace = (procSubID: number) => { categoryID: number; procID: number } | null;

export interface TemplateForm {
  title: string;
  description: string;
  membershipTotal: string;
  publicStatus: boolean;
  facilityIds: number[];
  membershipAgreementID: number;
  products: ProductRow[];
  procedures: ProcedureRow[];
  requiredDuration: string;
  cancelDuration: string;
  tagIds: number[];
  planTypeIds: number[];
  categoryIds: number[];
  packageType: number;
  availabilityType: 'unlimited' | 'date-range';
  availableTo: number;
  startDate: string;
  stopDate: string;
  maxUses: string;
  maxPatient: string;
}

let rowSeq = 0;
const nextKey = () => `row-${++rowSeq}`;

function expiryOf(days: number, months: number): { expire: string; unit: ExpireUnit } {
  if (months > 0) return months % 12 === 0 ? { expire: String(months / 12), unit: 'years' } : { expire: String(months), unit: 'months' };
  if (days > 0) return { expire: String(days), unit: 'days' };
  return { expire: '', unit: 'days' };
}

function expiryOut(expire: string, unit: ExpireUnit): { expireDays: number; expireMonths: number } {
  const n = Math.max(0, Math.round(Number(expire) || 0));
  if (unit === 'days') return { expireDays: n, expireMonths: 0 };
  return { expireDays: 0, expireMonths: unit === 'years' ? n * 12 : n };
}

function expiryLabel(expire: string, unit: ExpireUnit): string | null {
  const n = Number(expire);
  return n > 0 ? `Expires: ${n} ${n === 1 ? unit.slice(0, -1) : unit}` : null;
}

export function emptyTemplateForm(): TemplateForm {
  return {
    title: '',
    description: '',
    membershipTotal: '0',
    publicStatus: false,
    facilityIds: [],
    membershipAgreementID: 0,
    products: [],
    procedures: [],
    requiredDuration: '0',
    cancelDuration: '30',
    tagIds: [],
    planTypeIds: [],
    categoryIds: [],
    packageType: 0,
    availabilityType: 'unlimited',
    availableTo: 0,
    startDate: '',
    stopDate: '',
    maxUses: '0',
    maxPatient: '0',
  };
}

export function formFromDetail(
  d: TemplateDetail,
  availability: PackageAvailability | null,
  categoryOf: (productId: number) => string,
  placeOf: ProcedurePlace = () => null,
): TemplateForm {
  const a = availability;
  return {
    title: d.title,
    description: d.description,
    membershipTotal: String(d.membershipTotal),
    publicStatus: d.publicStatus,
    facilityIds: [...d.facilityIds],
    membershipAgreementID: d.membershipAgreementID,
    products: d.products.map((p) => ({
      key: nextKey(),
      productID: p.productID || null,
      category: categoryOf(p.productID),
      quantity: String(p.quantity),
      curRate: String(p.curRate),
      taxable: p.taxable,
      ...expiryOf(p.expireDays, p.expireMonths),
    })),
    procedures: d.procedures.map((p) => {
      // The catalog's own place for the procedure wins over the saved ids.
      const place = p.procSubID ? placeOf(p.procSubID) : null;
      return {
        key: nextKey(),
        categoryID: place?.categoryID ?? (p.categoryID || null),
        procID: place?.procID ?? (p.procID || null),
        procSubID: p.procSubID || null,
        savedName: p.name,
        savedCategoryName: p.categoryName,
        savedSubcategoryName: p.subcategoryName,
        description: p.description,
        hours: p.hours,
        price: String(p.price),
        effectivePrice: String(p.effectivePrice),
        expireDays: p.expireDays,
        expireMonths: p.expireMonths,
      };
    }),
    requiredDuration: String(d.requiredDuration),
    cancelDuration: String(d.cancelDuration),
    tagIds: [...d.tagIds],
    planTypeIds: [...d.planTypeIds],
    categoryIds: [...d.categoryIds],
    packageType: a?.packageType ?? 0,
    availabilityType: a?.type ?? 'unlimited',
    availableTo: a?.availableTo ?? 0,
    startDate: a?.startDate ?? '',
    stopDate: a?.stopDate ?? '',
    maxUses: String(a?.maxUses ?? 0),
    maxPatient: String(a?.maxPatient ?? 0),
  };
}

/** What the API is sent. Every list goes, empty ones as [] so clearing one saves. */
export function templatePayload(kind: TemplateKind, f: TemplateForm): TemplatePayload {
  const payload: TemplatePayload = {
    title: f.title.trim(),
    description: f.description.trim(),
    membershipTotal: Math.max(0, Number(f.membershipTotal) || 0),
    publicStatus: f.publicStatus,
    requiredDuration: kind === 'membership' ? Math.max(0, Math.round(Number(f.requiredDuration) || 0)) : 0,
    cancelDuration: kind === 'membership' ? Math.max(0, Math.round(Number(f.cancelDuration) || 0)) : 30,
    membershipAgreementID: kind === 'membership' ? f.membershipAgreementID : 0,
    products: f.products
      .filter((p) => p.productID)
      .map((p) => ({
        productID: p.productID as number,
        quantity: Math.max(1, Math.round(Number(p.quantity) || 1)),
        curRate: Number(p.curRate) || 0,
        taxable: p.taxable,
        ...expiryOut(p.expire, p.unit),
      })),
    procedures: f.procedures
      .filter((p) => p.procSubID)
      .map((p) => ({
        procSubID: p.procSubID as number,
        // The API fills in an unknown category / sub category from the procedure.
        ...(p.categoryID ? { categoryID: p.categoryID } : {}),
        ...(p.procID ? { procID: p.procID } : {}),
        quantity: 1,
        price: Number(p.price) || 0,
        effectivePrice: Number(p.effectivePrice) || 0,
        expNumHours: p.hours,
        laymansTerms: p.description.trim(),
        expireDays: p.expireDays,
        expireMonths: p.expireMonths,
      })),
    tags: [...f.tagIds],
    facilities: [...f.facilityIds],
    plan_types: [...f.planTypeIds],
  };
  if (kind === 'package') payload.product_categories = [...f.categoryIds];
  return payload;
}

export function availabilityOf(f: TemplateForm): PackageAvailability {
  return {
    packageType: f.packageType,
    type: f.availabilityType,
    availableTo: f.availableTo,
    startDate: f.startDate,
    stopDate: f.stopDate,
    maxUses: Math.max(0, Math.round(Number(f.maxUses) || 0)),
    maxPatient: Math.max(0, Math.round(Number(f.maxPatient) || 0)),
  };
}

/** Corp's checks: required (blocks saving) and suggestions. */
export function templateChecks(kind: TemplateKind, f: TemplateForm, hasImage: boolean): { errors: string[]; suggestions: string[] } {
  const errors: string[] = [];
  if (!f.title.trim()) errors.push('Title is required');
  if (f.publicStatus && !hasImage) errors.push('Image is required when available in store');
  if (f.products.some((p) => !p.productID)) errors.push('All products must have a product selected');
  if (f.procedures.some((p) => !p.procSubID)) errors.push('All procedures must have a procedure selected');
  if (kind === 'package' && f.availabilityType === 'date-range') {
    if (!f.startDate) errors.push('Start date is required for a date range');
    if (!f.stopDate) errors.push('Stop date is required for a date range');
  }
  const suggestions: string[] = [];
  if (f.products.length === 0 && f.procedures.length === 0) suggestions.push('No products or procedures selected');
  if (f.facilityIds.length === 0) suggestions.push('No facilities selected');
  if (!(Number(f.membershipTotal) > 0)) suggestions.push('Price is $0');
  return { errors, suggestions };
}

// ─── Options ───────────────────────────────────────────────────────────────────

interface ProductOption {
  id: number;
  name: string;
  category: string;
  productTypeId: number;
}

interface Options {
  products: ProductOption[];
  procedureTree: ProcedureCategoryOption[];
  /** The Hours choices (above 0) for a procedure row. */
  hourBands: number[];
  facilities: { id: number; label: string }[];
  tags: AdminPatientTag[];
  planTypes: { id: number; label: string }[];
  agreements: { value: number; label: string }[];
  categories: { id: number; label: string }[];
  typeCategoryLinks: ProductTypeCategoryLink[];
}

const UNCATEGORIZED = 'Uncategorized';

function procedurePlaceIn(tree: ProcedureCategoryOption[]): ProcedurePlace {
  return (procSubID) => {
    for (const c of tree) {
      for (const s of c.subcategories) {
        if (s.procedures.some((p) => p.procSubID === procSubID)) return { categoryID: c.categoryID, procID: s.procID };
      }
    }
    return null;
  };
}

/** A new, empty procedure row. */
function emptyProcedureRow(key: string): ProcedureRow {
  return {
    key,
    categoryID: null,
    procID: null,
    procSubID: null,
    savedName: '',
    savedCategoryName: '',
    savedSubcategoryName: '',
    description: '',
    hours: 0,
    price: '0',
    effectivePrice: '0',
    expireDays: 0,
    expireMonths: 0,
  };
}

const money = (n: number) => `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

async function loadOptions(kind: TemplateKind): Promise<Options> {
  const settle = <T,>(p: Promise<T>, fallback: T) => p.catch(() => fallback);
  const [products, procedureTree, hourBands, facilities, tags, planTypes, agreements, categories, links] = await Promise.all([
    settle(listAllProducts(), []),
    settle(listProcedureTree(), []),
    settle(listProcedureHourBands(), []),
    settle(listFacilities(), []),
    settle(listAdminPatientTags(), []),
    settle(listAdminPlanTypes(), []),
    kind === 'membership' ? settle(listAgreementVersions(), []) : Promise.resolve([]),
    kind === 'package' ? settle(listProductCategoriesLookup(), []) : Promise.resolve([]),
    kind === 'package' ? settle(listProductTypeCategoryLinks(), []) : Promise.resolve([]),
  ]);
  const seen = new Set<number>();
  return {
    products: products
      .filter((p) => !seen.has(p.quoteItemCostID) && seen.add(p.quoteItemCostID))
      .map((p) => ({
        id: p.quoteItemCostID,
        name: (p.strItem ?? '').trim() || `Product #${p.quoteItemCostID}`,
        category: p.relationships?.category?.title?.trim() || UNCATEGORIZED,
        productTypeId: p.relationships?.productTypeRelation?.productTypeID || p.relationships?.productAddon?.productTypeID || 0,
      }))
      .sort((a, b) => a.name.localeCompare(b.name)),
    // In the catalog's own order, as legacy's selects list them.
    procedureTree,
    hourBands,
    facilities: facilities
      .filter((f) => f.active)
      .map((f) => ({ id: f.facilityId, label: f.facilityName || `Facility #${f.facilityId}` }))
      .sort((a, b) => a.label.localeCompare(b.label)),
    tags: tags.filter((t) => t.active).sort((a, b) => a.title.localeCompare(b.title)),
    planTypes: planTypes.filter((p) => p.active).map((p) => ({ id: p.planTypeID, label: p.name || `Plan type #${p.planTypeID}` })),
    agreements: groupAgreements(agreements)
      .latest.filter((a) => a.active)
      .map((a) => ({ value: a.groupID, label: `${a.title} (v${a.version})` }))
      .sort((a, b) => a.label.localeCompare(b.label)),
    categories: categories
      .map((c) => ({ id: c.productCategoryID, label: (c.productCategoryName ?? '').trim() || `Category #${c.productCategoryID}` }))
      .sort((a, b) => a.label.localeCompare(b.label)),
    typeCategoryLinks: links,
  };
}

// ─── Modal ─────────────────────────────────────────────────────────────────────

export interface MembershipTemplateFormProps {
  kind: TemplateKind;
  /** The template to edit; null to add one. */
  templateId: number | null;
  onClose: () => void;
  onSaved: () => void;
  /** The image is assigned the moment it is picked (edit only). */
  onImageChanged?: (id: number, url: string) => void;
}

const EXPIRE_UNITS = [
  { value: 'days' as const, label: 'Days' },
  { value: 'months' as const, label: 'Months' },
  { value: 'years' as const, label: 'Years' },
];

const YES_NO = [
  { value: '0', label: 'No' },
  { value: '1', label: 'Yes' },
];

export function MembershipTemplateForm({ kind, templateId, onClose, onSaved, onImageChanged }: MembershipTemplateFormProps) {
  const isNew = templateId === null;
  const noun = kind === 'package' ? 'package' : 'membership';
  const locked = useAdminPageLevel() === 'locked';
  const t = useTrackedForm<TemplateForm>(emptyTemplateForm, { tracking: !isNew });
  const { reset } = t;
  const [options, setOptions] = useState<Options | null>(null);
  const [loaded, setLoaded] = useState(isNew);
  const [failed, setFailed] = useState(false);
  const [imageUrl, setImageUrl] = useState('');
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState<Set<string>>(new Set());

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const opts = await loadOptions(kind);
        if (cancelled) return;
        setOptions(opts);
        if (templateId !== null) {
          const [detail, availability] = await Promise.all([
            getTemplate(kind, templateId),
            kind === 'package' ? getPackageAvailability(templateId).catch(() => null) : Promise.resolve(null),
          ]);
          if (cancelled) return;
          const categoryOf = (id: number) => opts.products.find((p) => p.id === id)?.category ?? '';
          reset(formFromDetail(detail, availability, categoryOf, procedurePlaceIn(opts.procedureTree)));
          setImageUrl(detail.membershipImage);
          setLoaded(true);
        }
      } catch (err) {
        if (cancelled) return;
        setFailed(true);
        toast.error(getErrorMessage(err, `The ${noun} could not be loaded.`));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [kind, templateId, noun, reset]);

  const f = t.form;
  const { errors, suggestions } = templateChecks(kind, f, Boolean(imageUrl));
  const ready = loaded && options !== null;

  function patchProduct(key: string, next: Partial<ProductRow>) {
    t.patch({ products: f.products.map((r) => (r.key === key ? { ...r, ...next } : r)) });
  }
  function patchProcedure(key: string, next: Partial<ProcedureRow>) {
    t.patch({ procedures: f.procedures.map((r) => (r.key === key ? { ...r, ...next } : r)) });
  }
  function toggleOpen(key: string) {
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  // Package categories that come with the chosen products' types, read-only.
  const inherited = useMemo(() => {
    if (kind !== 'package' || !options) return [];
    const byCategory = new Map<number, { id: number; name: string; products: string[] }>();
    for (const row of f.products) {
      const product = options.products.find((p) => p.id === row.productID);
      if (!product || !product.productTypeId) continue;
      for (const link of options.typeCategoryLinks) {
        if (link.productTypeID !== product.productTypeId) continue;
        const entry = byCategory.get(link.categoryID) ?? { id: link.categoryID, name: link.name || `Category ${link.categoryID}`, products: [] };
        if (!entry.products.includes(product.name)) entry.products.push(product.name);
        byCategory.set(link.categoryID, entry);
      }
    }
    return [...byCategory.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [kind, options, f.products]);

  async function save() {
    if (errors.length > 0 || !ready) return;
    setSaving(true);
    const payload = templatePayload(kind, f);
    try {
      let id = templateId;
      if (id === null) {
        id = await createTemplate(kind, imageUrl ? { ...payload, membershipImage: imageUrl } : payload);
      } else {
        await updateTemplate(id, payload);
      }
      if (kind === 'package' && id) {
        try {
          await savePackageAvailability(id, availabilityOf(f));
        } catch (err) {
          toast.error(getErrorMessage(err, 'The package was saved, but its availability settings were not.'));
        }
      }
      toast.success(`${kind === 'package' ? 'Package' : 'Membership'} ${isNew ? 'created' : 'updated'}`);
      onSaved();
    } catch (err) {
      toast.error(getErrorMessage(err, `The ${noun} could not be saved.`));
    } finally {
      setSaving(false);
    }
  }

  // The footer sits outside the modal's locked body, so the Total locks itself.
  const total = (
    <AdminLockedFieldset>
      <div className="flex items-center gap-1.5">
        <label htmlFor="template-total" className="text-sm font-medium text-slate-700">
          Total:
        </label>
        {t.badge('membershipTotal', 'Total')}
        <div
          className={cn(
            'flex items-center gap-1 rounded-md px-1',
            !(Number(f.membershipTotal) > 0) && 'border border-amber-300 bg-amber-50/30',
          )}
        >
          <span className="text-sm text-slate-500">$</span>
          <Input
            id="template-total"
            type="number"
            step="0.01"
            min="0"
            value={f.membershipTotal}
            placeholder="0.00"
            onChange={(e) => t.patch({ membershipTotal: e.target.value })}
            wrapperClassName="w-28"
            disabled={!ready}
          />
          {!(Number(f.membershipTotal) > 0) && <span className="text-xs font-medium text-amber-600">$0</span>}
        </div>
      </div>
    </AdminLockedFieldset>
  );

  const usedProducts = new Set(f.products.map((p) => p.productID).filter(Boolean) as number[]);
  const categoryChoices = options ? [...new Set(options.products.map((p) => p.category))].sort().map((c) => ({ value: c, label: c })) : [];
  const noItems = f.products.length === 0 && f.procedures.length === 0;

  return (
    <AdminFormModal
      title={isNew ? `Add ${noun}` : locked ? (kind === 'package' ? 'Package' : 'Membership') : `Edit ${noun}`}
      description={isNew ? `Create a new ${noun} template.` : locked ? `The ${noun} template's details.` : `Update ${noun} template details.`}
      isNew={isNew}
      dirty={isNew ? t.changed.size > 0 || Boolean(imageUrl) : t.dirty}
      saving={saving}
      errors={ready ? errors : []}
      suggestions={ready ? suggestions : []}
      canSave={ready}
      footerExtra={total}
      onSave={() => void save()}
      onRevertAll={t.revertAll}
      onClose={onClose}
      size="xl"
    >
      {failed ? (
        <p className="py-12 text-center text-sm text-slate-500">This {noun} couldn't be loaded. Close and reopen it to try again.</p>
      ) : !ready ? (
        <div className="flex flex-col items-center justify-center space-y-4 py-12">
          <Spinner />
          <p className="text-sm text-slate-500">Loading {noun} details...</p>
        </div>
      ) : (
        <div className="flex flex-col gap-6 md:flex-row">
          {/* Left: image and facilities */}
          <div className="space-y-4 md:w-48 md:shrink-0">
            <EntityImageTile
              entityType="membership"
              entityId={templateId}
              folder={kind === 'package' ? 'packages' : 'memberships'}
              imageUrl={imageUrl}
              label={kind === 'package' ? 'Package Image' : 'Membership Image'}
              required={f.publicStatus}
              onSelected={(url) => {
                setImageUrl(url);
                if (templateId !== null) onImageChanged?.(templateId, url);
              }}
            />
            {/* Corp: none selected puts the list in an amber box, the warning above the label. */}
            <div className={cn('space-y-2', f.facilityIds.length === 0 && fieldClasses.wrapper('warning'))} data-testid="template-facilities">
              {f.facilityIds.length === 0 && <p className={fieldClasses.message('warning')}>No facilities selected</p>}
              <span className={LABEL_CLASS}>
                <span>Facilities</span>
                {t.badge('facilityIds', 'Facilities')}
              </span>
              {options!.facilities.length === 0 ? (
                <p className={DESCRIPTION_CLASS}>No facilities available.</p>
              ) : (
                <CheckboxList
                  idPrefix="template-facility"
                  items={options!.facilities}
                  selected={f.facilityIds}
                  onChange={(facilityIds) => t.patch({ facilityIds })}
                />
              )}
            </div>
          </div>

          {/* Right */}
          <div className="min-w-0 flex-1 space-y-4">
            <DialogField
              label="Title"
              htmlFor="template-title"
              required
              state={f.title.trim() ? 'none' : 'error'}
              labelHint={f.title.trim() ? undefined : '— required'}
              badge={t.badge('title', 'Title')}
            >
              <Input
                id="template-title"
                value={f.title}
                maxLength={255}
                placeholder={kind === 'package' ? 'e.g. Premium Package' : 'e.g. Premium Membership'}
                onChange={(e) => t.patch({ title: e.target.value })}
                className={fieldClasses.input(f.title.trim() ? 'none' : 'error')}
              />
            </DialogField>
            <DialogField label="Description" htmlFor="template-description" badge={t.badge('description', 'Description')}>
              <RichDescriptionField id="template-description" value={f.description} onChange={(html) => t.patch({ description: html })} dialogTitle={kind === 'package' ? 'Edit Package Description' : 'Edit Membership Description'} />
            </DialogField>
            <SwitchField
              id="template-store"
              label="Available in Store"
              checked={f.publicStatus}
              onChange={(v) => t.patch({ publicStatus: v })}
              badge={t.badge('publicStatus', 'Available in Store')}
            />

            {kind === 'membership' && (
              <DialogField label="Membership Agreement" htmlFor="template-agreement" badge={t.badge('membershipAgreementID', 'Membership Agreement')}>
                <Select<number>
                  id="template-agreement"
                  options={(() => {
                    const out = [{ value: 0, label: 'None' }, ...options!.agreements];
                    if (f.membershipAgreementID && !out.some((o) => o.value === f.membershipAgreementID)) {
                      out.push({ value: f.membershipAgreementID, label: `Agreement #${f.membershipAgreementID} (deleted)` });
                    }
                    return out;
                  })()}
                  value={f.membershipAgreementID}
                  onChange={(v) => t.patch({ membershipAgreementID: v ?? 0 })}
                />
              </DialogField>
            )}

            {/* Products */}
            <ItemsBox
              label="Products"
              addLabel="Add product"
              warn={noItems}
              badge={t.badge('products', 'Products')}
              onAdd={() => {
                const key = nextKey();
                t.patch({ products: [...f.products, { key, productID: null, category: '', quantity: '1', curRate: '0', taxable: false, expire: '', unit: 'days' }] });
                setOpen((prev) => new Set(prev).add(key));
              }}
            >
              {f.products.map((row) => {
                const product = options!.products.find((p) => p.id === row.productID);
                const choices = options!.products
                  .filter((p) => (!row.category || p.category === row.category) && (p.id === row.productID || !usedProducts.has(p.id)))
                  .map((p) => ({ value: p.id, label: p.name }));
                return (
                  <ItemRow
                    key={row.key}
                    title={product?.name ?? null}
                    missing="(product required)"
                    meta={`Qty: ${Number(row.quantity) || 1}`}
                    aside={expiryLabel(row.expire, row.unit)}
                    open={open.has(row.key)}
                    onToggle={() => toggleOpen(row.key)}
                    removeLabel={`Remove product ${product?.name ?? ''}`.trim()}
                    onRemove={() => t.patch({ products: f.products.filter((r) => r.key !== row.key) })}
                  >
                    {/* Corp: Category, Product and the expiry down three quarters; Fee, Quantity, Taxable beside them. */}
                    <div className="grid gap-3 md:grid-cols-4">
                      <div className="min-w-0 space-y-3 md:col-span-3">
                        <DialogField label="Category" htmlFor={`${row.key}-category`}>
                          <SearchableCombobox<string>
                            id={`${row.key}-category`}
                            options={categoryChoices}
                            value={row.category || null}
                            onChange={(v) => patchProduct(row.key, { category: v ?? '' })}
                            placeholder="Select category"
                            clearable
                          />
                        </DialogField>
                        <DialogField label="Product" htmlFor={`${row.key}-product`} state={row.productID ? 'none' : 'error'}>
                          <SearchableCombobox<number>
                            id={`${row.key}-product`}
                            options={choices}
                            value={row.productID}
                            onChange={(v) => {
                              const picked = options!.products.find((p) => p.id === v);
                              patchProduct(row.key, { productID: v ?? null, category: picked?.category ?? row.category });
                            }}
                            placeholder="Select product"
                          />
                        </DialogField>
                        <ExpiryFields
                          id={row.key}
                          expire={row.expire}
                          unit={row.unit}
                          onChange={(next) => patchProduct(row.key, next)}
                        />
                      </div>
                      <div className="min-w-0 space-y-3">
                        <DialogField label="Fee" htmlFor={`${row.key}-fee`}>
                          <Input id={`${row.key}-fee`} type="number" step="0.01" min="0" value={row.curRate} onChange={(e) => patchProduct(row.key, { curRate: e.target.value })} />
                        </DialogField>
                        <DialogField label="Quantity" htmlFor={`${row.key}-quantity`}>
                          <Input
                            id={`${row.key}-quantity`}
                            type="number"
                            min="1"
                            value={row.quantity}
                            onChange={(e) => patchProduct(row.key, { quantity: e.target.value })}
                            onBlur={() => {
                              if (!(Number(row.quantity) >= 1)) patchProduct(row.key, { quantity: '1' });
                            }}
                          />
                        </DialogField>
                        <DialogField label="Taxable" htmlFor={`${row.key}-taxable`}>
                          <Select
                            id={`${row.key}-taxable`}
                            options={YES_NO}
                            value={row.taxable ? '1' : '0'}
                            onChange={(v) => patchProduct(row.key, { taxable: v === '1' })}
                          />
                        </DialogField>
                      </div>
                    </div>
                  </ItemRow>
                );
              })}
            </ItemsBox>

            {/* Procedures */}
            <ItemsBox
              label="Procedures"
              addLabel="Add procedure"
              warn={noItems}
              badge={t.badge('procedures', 'Procedures')}
              onAdd={() => {
                const key = nextKey();
                t.patch({ procedures: [...f.procedures, emptyProcedureRow(key)] });
                setOpen((prev) => new Set(prev).add(key));
              }}
            >
              {f.procedures.map((row) => {
                const name = procedureNameOf(row, options!.procedureTree);
                return (
                  <ItemRow
                    key={row.key}
                    title={name}
                    missing="(procedure required)"
                    // Legacy's row header shows the effective price.
                    meta={row.procSubID ? money(Number(row.effectivePrice) || 0) : null}
                    open={open.has(row.key)}
                    onToggle={() => toggleOpen(row.key)}
                    removeLabel={`Remove procedure ${name ?? ''}`.trim()}
                    onRemove={() => t.patch({ procedures: f.procedures.filter((r) => r.key !== row.key) })}
                  >
                    <ProcedureFields row={row} tree={options!.procedureTree} hourBands={options!.hourBands} onPatch={(next) => patchProcedure(row.key, next)} />
                  </ItemRow>
                );
              })}
            </ItemsBox>

            {kind === 'package' && (
              <section className="space-y-3 rounded-md border border-slate-200 bg-slate-50/50 p-4" aria-label="Availability Settings">
                <span className={LABEL_CLASS}>
                  <span>Availability Settings</span>
                </span>
                <div className="grid gap-3 md:grid-cols-2">
                  <DialogField label="Package Type" htmlFor="package-type" badge={t.badge('packageType', 'Package Type')}>
                    <Select<number>
                      id="package-type"
                      options={[
                        { value: 0, label: 'Standard' },
                        { value: 1, label: 'Promo' },
                      ]}
                      value={f.packageType}
                      onChange={(v) => t.patch({ packageType: v ?? 0 })}
                    />
                  </DialogField>
                  <DialogField label="Available To" htmlFor="package-available-to" badge={t.badge('availableTo', 'Available To')}>
                    <Select<number>
                      id="package-available-to"
                      options={[
                        { value: 0, label: 'Everyone' },
                        { value: 1, label: 'Non-Members' },
                        { value: 2, label: 'Members' },
                      ]}
                      value={f.availableTo}
                      onChange={(v) => t.patch({ availableTo: v ?? 0 })}
                    />
                  </DialogField>
                </div>
                <DialogField label="Availability Dates" htmlFor="package-dates" badge={t.badge('availabilityType', 'Availability Dates')}>
                  <Select<'unlimited' | 'date-range'>
                    id="package-dates"
                    options={[
                      { value: 'unlimited', label: 'Unlimited' },
                      { value: 'date-range', label: 'Date Range' },
                    ]}
                    value={f.availabilityType}
                    onChange={(v) => v && t.patch({ availabilityType: v })}
                  />
                </DialogField>
                {f.availabilityType === 'date-range' && (
                  <div className="grid gap-3 md:grid-cols-2">
                    <DialogField
                      label="Start Date"
                      htmlFor="package-start"
                      state={f.startDate ? 'none' : 'error'}
                      labelHint={f.startDate ? undefined : '— required'}
                      badge={t.badge('startDate', 'Start Date')}
                    >
                      <DatePicker id="package-start" value={f.startDate} onChange={(v) => t.patch({ startDate: v })} />
                    </DialogField>
                    <DialogField
                      label="Stop Date"
                      htmlFor="package-stop"
                      state={f.stopDate ? 'none' : 'error'}
                      labelHint={f.stopDate ? undefined : '— required'}
                      badge={t.badge('stopDate', 'Stop Date')}
                    >
                      <DatePicker id="package-stop" value={f.stopDate} onChange={(v) => t.patch({ stopDate: v })} min={f.startDate || undefined} />
                    </DialogField>
                  </div>
                )}
                <div className="grid gap-3 md:grid-cols-2">
                  <DialogField label="Max Uses" htmlFor="package-max-uses" description="0 = unlimited" badge={t.badge('maxUses', 'Max Uses')}>
                    <Input id="package-max-uses" type="number" min="0" value={f.maxUses} placeholder="0 = unlimited" onChange={(e) => t.patch({ maxUses: e.target.value })} />
                  </DialogField>
                  <DialogField
                    label="Max Per Patient"
                    htmlFor="package-max-patient"
                    description="0 = unlimited per patient"
                    badge={t.badge('maxPatient', 'Max Per Patient')}
                  >
                    <Input
                      id="package-max-patient"
                      type="number"
                      min="0"
                      value={f.maxPatient}
                      placeholder="0 = unlimited"
                      onChange={(e) => t.patch({ maxPatient: e.target.value })}
                    />
                  </DialogField>
                </div>
              </section>
            )}

            {kind === 'membership' && (
              <section className="space-y-3" aria-label="Cancellation Policy">
                <span className={LABEL_CLASS}>
                  <span>Cancellation Policy</span>
                </span>
                <DaysField
                  id="template-required-duration"
                  label="Required Duration:"
                  value={f.requiredDuration}
                  onChange={(requiredDuration) => t.patch({ requiredDuration })}
                  badge={t.badge('requiredDuration', 'Required Duration')}
                  hint="Minimum number of days the member must remain enrolled before they can cancel."
                />
                <DaysField
                  id="template-cancel-notice"
                  label="Cancel Notice:"
                  value={f.cancelDuration}
                  onChange={(cancelDuration) => t.patch({ cancelDuration })}
                  badge={t.badge('cancelDuration', 'Cancel Notice')}
                  hint="Number of days advance notice required before a cancellation takes effect."
                />
              </section>
            )}

            {/* Tags */}
            <section className="space-y-2" aria-label="Tags">
              <span className={LABEL_CLASS}>
                <span>Tags</span>
                {t.badge('tagIds', 'Tags')}
              </span>
              <div className="flex flex-col gap-3 md:flex-row">
                <div className="flex min-h-[3rem] flex-wrap content-start gap-2 rounded-md border border-dashed border-slate-200 p-2 md:w-2/5">
                  {f.tagIds.length === 0 ? (
                    <span className="text-xs text-slate-400">Selected tag images will appear here</span>
                  ) : (
                    f.tagIds.map((id) => {
                      const tag = options!.tags.find((x) => x.tagsCustomID === id);
                      return <TagIconImage key={id} path={tag?.icon || null} size={28} />;
                    })
                  )}
                </div>
                <div className="min-w-0 flex-1 space-y-2">
                  {options!.tags.length === 0 ? (
                    <p className={DESCRIPTION_CLASS}>No tags available.</p>
                  ) : (
                    <>
                      {!locked && (
                        <div className="flex gap-1">
                          <Button variant="secondary" size="xs" className="h-7 px-2" onClick={() => t.patch({ tagIds: options!.tags.map((x) => x.tagsCustomID) })}>
                            Select All
                          </Button>
                          <Button variant="secondary" size="xs" className="h-7 px-2" onClick={() => t.patch({ tagIds: [] })}>
                            Deselect All
                          </Button>
                        </div>
                      )}
                      <div className="max-h-48 space-y-1.5 overflow-y-auto">
                        {options!.tags.map((tag) => {
                          const id = `template-tag-${tag.tagsCustomID}`;
                          const checked = f.tagIds.includes(tag.tagsCustomID);
                          return (
                            <div key={tag.tagsCustomID} className="flex items-center gap-2">
                              <Checkbox
                                id={id}
                                checked={checked}
                                onChange={(e) =>
                                  t.patch({
                                    tagIds: e.target.checked ? [...f.tagIds, tag.tagsCustomID] : f.tagIds.filter((x) => x !== tag.tagsCustomID),
                                  })
                                }
                                wrapperClassName="items-center gap-2"
                              />
                              <TagIconImage path={tag.icon || null} size={16} />
                              <label htmlFor={id} className="cursor-pointer text-sm text-slate-900">
                                {tag.title}
                              </label>
                            </div>
                          );
                        })}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </section>

            {/* Plan types */}
            <div className="space-y-2">
              <label htmlFor="template-plan-types" className={LABEL_CLASS}>
                <span>Plan types</span>
                {t.badge('planTypeIds', 'Plan types')}
              </label>
              {options!.planTypes.length === 0 ? (
                <p className={DESCRIPTION_CLASS}>No plan types available.</p>
              ) : (
                <>
                  <SearchableMultiSelect
                    id="template-plan-types"
                    options={options!.planTypes.map((p) => ({ value: String(p.id), label: p.label }))}
                    selectedValues={f.planTypeIds.map(String)}
                    onSelectionChange={(v) => t.patch({ planTypeIds: v.map(Number) })}
                    placeholder="No plan types"
                    searchPlaceholder="Search plan types…"
                  />
                  <SelectedRows
                    rows={f.planTypeIds.map((id) => options!.planTypes.find((p) => p.id === id) ?? { id, label: `Plan type #${id}`, unavailable: true })}
                    removeLabel="Remove"
                    onRemove={(id) => t.patch({ planTypeIds: f.planTypeIds.filter((x) => x !== id) })}
                  />
                </>
              )}
            </div>

            {kind === 'package' && (
              <div className="space-y-2">
                <label htmlFor="template-categories" className={LABEL_CLASS}>
                  <span>Product categories</span>
                  {t.badge('categoryIds', 'Product categories')}
                </label>
                {inherited.length > 0 && (
                  <ul className="space-y-1" data-testid="inherited-categories">
                    {inherited.map((c) => (
                      <li key={c.id} className="rounded border border-slate-100 bg-slate-50 px-2 py-1 text-xs text-slate-600">
                        <span className="font-medium text-slate-800">{c.name}</span> inherited from {c.products.join(', ')}
                      </li>
                    ))}
                  </ul>
                )}
                <SearchableMultiSelect
                  id="template-categories"
                  options={options!.categories.map((c) => ({ value: String(c.id), label: c.label }))}
                  selectedValues={f.categoryIds.map(String)}
                  onSelectionChange={(v) => t.patch({ categoryIds: v.map(Number) })}
                  placeholder="No categories"
                  searchPlaceholder="Search categories…"
                />
                <SelectedRows
                  rows={f.categoryIds.map((id) => options!.categories.find((c) => c.id === id) ?? { id, label: `Category #${id}`, unavailable: true })}
                  removeLabel="Remove"
                  onRemove={(id) => t.patch({ categoryIds: f.categoryIds.filter((x) => x !== id) })}
                />
              </div>
            )}
          </div>
        </div>
      )}
    </AdminFormModal>
  );
}

// ─── Pieces ────────────────────────────────────────────────────────────────────

function ExpiryFields({
  id,
  expire,
  unit,
  onChange,
}: {
  id: string;
  expire: string;
  unit: ExpireUnit;
  onChange: (next: { expire?: string; unit?: ExpireUnit }) => void;
}) {
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      <DialogField label="Expire" htmlFor={`${id}-expire`}>
        <Input id={`${id}-expire`} type="number" min="0" value={expire} onChange={(e) => onChange({ expire: e.target.value })} />
      </DialogField>
      <DialogField label="Unit" htmlFor={`${id}-unit`}>
        <Select<ExpireUnit> id={`${id}-unit`} options={EXPIRE_UNITS} value={unit} onChange={(v) => v && onChange({ unit: v })} />
      </DialogField>
    </div>
  );
}

function DaysField({
  id,
  label,
  value,
  onChange,
  badge,
  hint,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  badge: ReactNode;
  hint: string;
}) {
  return (
    <div className="space-y-1">
      <div className="flex flex-wrap items-center gap-2">
        <label htmlFor={id} className="text-sm text-slate-700">
          {label}
        </label>
        <Input id={id} type="number" min="0" value={value} onChange={(e) => onChange(e.target.value)} wrapperClassName="w-24" />
        <span className="text-sm text-slate-500">days</span>
        {badge}
      </div>
      <p className={DESCRIPTION_CLASS}>{hint}</p>
    </div>
  );
}
